import React, { useCallback, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

const PANEL_WIDTH = 500;

interface SidePeekProps {
  title: string;
  onClose: () => void;
  children?: React.ReactNode;
}

const SidePeek: React.FC<SidePeekProps> = ({
  title,
  onClose,
  children,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(timer);
  }, []);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!panelRef.current || panelRef.current.contains(target)) {
        return;
      }

      if (target.closest(".max-h-60") || target.closest(".z-\\[9999\\]")) {
        return;
      }

      handleClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClose]);

  return (
    <div
      ref={panelRef}
      className={`fixed bottom-0 right-0 top-0 z-[130] flex flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out ${
        isVisible ? "translate-x-0" : "translate-x-full"
      }`}
      style={{
        top: "var(--maintenance-banner-height, 0px)",
        height: "calc(100vh - var(--maintenance-banner-height, 0px))",
        width: PANEL_WIDTH,
      }}
    >
      <div className="flex items-center justify-between self-stretch bg-[#F9FAFB] p-4">
        <span className="text-[14px] font-medium leading-5 text-[#070B24]">
          {title}
        </span>

        <button
          type="button"
          onClick={handleClose}
          className="flex h-6 w-6 items-center justify-center text-[#737373]"
        >
          <X size={16} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

export default SidePeek;